import { useState, useEffect, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useTraccarSession } from '@/hooks/useTraccarSession';
import { useTraccarWebSocket } from '@/hooks/useTraccarWebSocket';
import { DeviceMap } from '@/components/dashboard/DeviceMap';
import { fetchDevices, fetchPositions, type Position } from '@/lib/traccar-api';
import { fetchDeviceMerges } from '@/lib/reseller-api';
import { useResellerAccess } from '@/hooks/useResellerAccess';

const FullMap = () => {
  const { isAuthenticated } = useTraccarSession();
  const { isReseller } = useResellerAccess();

  const {
    positions: wsPositions,
    devices: wsDevices,
    connect: wsConnect,
    disconnect: wsDisconnect,
  } = useTraccarWebSocket();

  const { data: initialDevices = [], isLoading: devicesLoading } = useQuery({
    queryKey: ['devices'],
    queryFn: fetchDevices,
    enabled: isAuthenticated,
  });

  const { data: initialPositions = [], isLoading: positionsLoading } = useQuery({
    queryKey: ['positions'],
    queryFn: fetchPositions,
    enabled: isAuthenticated,
    staleTime: 10000,
  });

  const { data: deviceMerges = [] } = useQuery({
    queryKey: ['device-merges'],
    queryFn: fetchDeviceMerges,
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const allDevices = useMemo(() => {
    if (wsDevices.length === 0) return initialDevices;
    const deviceMap = new Map(initialDevices.map(d => [d.id, d]));
    wsDevices.forEach(d => deviceMap.set(d.id, d));
    return Array.from(deviceMap.values());
  }, [initialDevices, wsDevices]);

  const devices = useMemo(() => {
    if (isReseller) return allDevices;
    const secondaryIds = new Set(deviceMerges.map(m => m.secondary_device_id));
    return allDevices.filter(d => !secondaryIds.has(d.id));
  }, [allDevices, isReseller, deviceMerges]);

  const [positions, setPositions] = useState<Position[]>([]);

  useEffect(() => {
    if (initialPositions.length > 0 && positions.length === 0) {
      setPositions(initialPositions);
    }
  }, [initialPositions, positions.length]);

  useEffect(() => {
    if (wsPositions.length > 0) {
      setPositions(prev => {
        const updated = new Map(prev.map(p => [p.deviceId, p]));
        wsPositions.forEach(p => updated.set(p.deviceId, p));
        return Array.from(updated.values());
      });
    }
  }, [wsPositions]);

  useEffect(() => {
    wsConnect();
    return () => { wsDisconnect(); };
  }, [wsConnect, wsDisconnect]);

  const effectivePositions = useMemo(() => {
    if (isReseller) return positions;
    const posMap = new Map(positions.map(p => [p.deviceId, p]));
    deviceMerges.forEach(merge => {
      const primaryPos = posMap.get(merge.primary_device_id);
      const secondaryPos = posMap.get(merge.secondary_device_id);
      if (secondaryPos) {
        const primaryTime = primaryPos ? new Date(primaryPos.fixTime).getTime() : 0;
        const secondaryTime = new Date(secondaryPos.fixTime).getTime();
        if (secondaryTime > primaryTime) {
          posMap.set(merge.primary_device_id, { ...secondaryPos, deviceId: merge.primary_device_id });
        }
      }
    });
    return Array.from(posMap.values());
  }, [positions, isReseller, deviceMerges]);

  const isLoading = devicesLoading || positionsLoading;

  if (isLoading && positions.length === 0) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-dvh w-dvw">
      <DeviceMap
        devices={devices}
        positions={effectivePositions}
        compact={true}
      />
    </div>
  );
};

export default FullMap;
